import React, { useEffect, useRef, useState } from 'react';

const CloudsBackground = () => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!dimensions.width || !dimensions.height) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size - full width, 30% height (like stars)
    canvas.width = dimensions.width;
    canvas.height = dimensions.height * 0.3;

    // Cloud parameters
    const cloudCount = 6;
    const clouds = [];

    // Generate a single cloud shape (randomized blobs)
    const createCloudShape = (size) => {
      const points = [];
      const segments = 8;
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const radiusVariation = 0.2 + Math.random() * 0.3;
        points.push({
          x: Math.cos(angle) * size * (0.7 + radiusVariation),
          y: Math.sin(angle) * size * (0.5 + radiusVariation * 0.5),
        });
      }
      return points;
    };

    // Initialize clouds with different layers (for parallax)
    for (let i = 0; i < cloudCount; i++) {
      const size = Math.random() * 60 + 40;
      const speed = Math.random() * 0.1 + 0.05;
      const layer = Math.floor(Math.random() * 3); // 0 (far), 1 (mid), 2 (near)
      const opacity = 0.5 + layer * 0.2; // Increased opacity for visibility

      clouds.push({
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height * 0.2,
        size: size,
        shape: createCloudShape(size),
        speed: speed * (layer + 1), // Near clouds move faster (parallax)
        layer: layer,
        opacity: opacity,
      });
    }

    // Add some guaranteed clouds in nice positions
    clouds.push(
      {
        x: dimensions.width * 0.1,
        y: dimensions.height * 0.05,
        size: 80,
        shape: createCloudShape(80),
        speed: 0.08,
        layer: 2,
        opacity: 0.9,
      },
      {
        x: dimensions.width * 0.6,
        y: dimensions.height * 0.1,
        size: 120,
        shape: createCloudShape(120),
        speed: 0.04,
        layer: 0,
        opacity: 0.7,
      }
    );

    let animationId;
    let lastTime = 0;

    const drawClouds = (timestamp) => {
      if (!lastTime) lastTime = timestamp;
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      clouds.forEach((cloud) => {
        // Update position (horizontal drift)
        cloud.x += cloud.speed;

        // Reset cloud position if it goes off screen
        if (cloud.x > dimensions.width + cloud.size * 2) {
          cloud.x = -cloud.size * 2;
          cloud.y = Math.random() * dimensions.height * 0.2;
        }

        // Draw cloud
        ctx.save();
        ctx.translate(cloud.x, cloud.y);

        // Exactly the color you specified: rgba(226, 234, 233)
        const cloudColor = `rgba(226, 234, 233, ${cloud.opacity})`;
        ctx.fillStyle = cloudColor;

        ctx.beginPath();

        // Draw cloud shape (rounded blob)
        ctx.moveTo(cloud.shape[0].x, cloud.shape[0].y);
        for (let i = 1; i < cloud.shape.length; i++) {
          const p = cloud.shape[i];
          ctx.lineTo(p.x, p.y);
        }
        ctx.closePath();
        ctx.fill();

        ctx.restore();
      });

      animationId = requestAnimationFrame(drawClouds);
    };

    animationId = requestAnimationFrame(drawClouds);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [dimensions]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '30%',
        pointerEvents: 'none',
        zIndex: 1,
        // Removed blend mode for pure color visibility
        // Gradient mask to fade out at bottom
        maskImage: 'linear-gradient(to bottom, white 60%, transparent 85%)',
        WebkitMaskImage:
          'linear-gradient(to bottom, white 60%, transparent 85%)',
      }}
    />
  );
};

export default CloudsBackground;
